import React, { useEffect, useState } from 'react';
import { Stack, Paper, Typography, Table, TableBody, TableCell, TableRow, Chip, Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment-timezone';
import { dashboardUrl } from '../../service/config';
import { EditJob } from './edit_job';
import { OverflowTooltip } from '../overflow_tooltip';
import convertSeconds from '../../utils/convertSeconds';
import calenderIcon from '../../assets/ion_calendar-outline.svg';
import timerIcon from '../../assets/ic_sharp-access-time.svg';
import minisIcon from '../../assets/ic_twotone-running-with-errors.svg';
import '../jobs/overview.css';
import AnalyticsVisualization from '../framework/analyticsVisualization';

export function Overview({ jobId, afterUpdate, jobOverview }: any) {
	const [jobData, setJobData] = useState<any>({});
	const [editOpen, setEditOpen] = React.useState(false);

	moment.updateLocale(moment.locale(), { invalidDate: '' });

	const handleEditOpen = () => {
		setEditOpen(true);
	};

	const handleEditClose = () => {
		setEditOpen(false);
		afterUpdate();
	};

	useEffect(() => {
		fetchJobData();
		const url = window.location.pathname;
		const activeId = document.getElementById('root');
		const jobName = url.substring(url.lastIndexOf('/') + 1);
		if (window.location.pathname === '/jobs') {
			if (activeId) {
				activeId.classList.add('jobs-list-table');
				activeId.classList.remove('jobs-list');
				activeId.classList.remove('root');
				document.body.classList.add('jobs-list-datas');
			}
		} else if (jobName !== window.location.pathname && window.location.pathname === '/jobs/' + jobName) {
			if (activeId) {
				activeId.classList.add('jobs-list');
				activeId.classList.remove('jobs-list-table');
				activeId.classList.remove('root');
				document.body.classList.remove('jobs-list-datas');
			}
		} else {
			if (activeId) {
				activeId.classList.add('root');
				activeId.classList.remove('jobs-list-table');
				activeId.classList.remove('jobs-list');
				document.body.classList.remove('jobs-list-datas');
			}
		}
	}, [jobOverview]);

	function fetchJobData() {
		setJobData(jobOverview);
	}

	const renderStatus = (status: any) => {
		switch (status) {
			case 'SUCCESS':
				return <Chip label={status} color="success" />;
			case 'FAILURE':
			case 'TERMINATED':
				return <Chip label={status} color="secondary" />;
			case 'NOT STARTED':
				return <Chip label={status} color="warning" />;
			case 'RUNNING':
				return <Chip label={status} style={{ backgroundColor: '#299BFF26', color: '#299BFF' }} />;
			default:
				return <Chip label={status} style={{ backgroundColor: '#1B1E35CC', color: '#71758F' }} />;
		}
	};

	return (
		<Stack
			direction="column"
			spacing={2}
			sx={{
				paddingBottom: 0,
				marginBottom: '50px',
				'& .MuiTableCell-root': {
					paddingBottom: '20px !important'
				},
				'& .emptyCell': {
					width: '20px'
				}
			}}
		>
			<Stack direction="row" alignItems="flextStart" spacing={2}>
				<Paper elevation={1} className="box-panel width-65 h-360">
					<Box p={2}>
						<Typography variant="subHeader" component="h6">
							Job Details
						</Typography>
						<Table className="job-overview-table">
							<TableBody>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Job Name</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.name}</Typography>
									</TableCell>
									<TableCell className="emptyCell"></TableCell>
									<React.Fragment>
										{jobData.scheduleDays === '' ? (
											<React.Fragment>
												<TableCell className="attrTitle">
													<Typography variant="bodyText">Schedule Dates</Typography>
												</TableCell>
												<TableCell className="attrValue">
													<Typography variant="bodyText">
														{jobData.scheduleDate}{' '}
														{jobData.otherScheduleInfo && <Box mt={2}>{JSON.parse(jobData.otherScheduleInfo).dates}</Box>}
													</Typography>
												</TableCell>
											</React.Fragment>
										) : (
											<React.Fragment>
												<TableCell className="attrTitle">
													<Typography variant="bodyText">Schedule Days</Typography>
												</TableCell>
												<TableCell className="attrValue">
													<Typography variant="bodyText">
														{jobData.scheduleDays}
														{jobData.otherScheduleInfo && (
															<Box mt={2}>{JSON.parse(jobData.otherScheduleInfo).day.replace(/[\[\]"]/g, '')}</Box>
														)}
													</Typography>
												</TableCell>
											</React.Fragment>
										)}
									</React.Fragment>
								</TableRow>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Observed Since</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											<p className="cal-txt">{jobData.createdAt && moment(jobData.createdAt).format()}</p>
											<img src={calenderIcon} className="cal-icon" />
										</Typography>
									</TableCell>
									<TableCell className="emptyCell"></TableCell>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Run Window</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											<p className="cal-txt">
												{jobData.runWindow}
												{jobData.otherScheduleInfo && <Box mt={2}>{JSON.parse(jobData.otherScheduleInfo).runWindow}</Box>}
											</p>
										</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<img src={timerIcon} className="cal-icon" />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Scheduler</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.schedulerName}</Typography>
									</TableCell>
									<TableCell className="emptyCell"></TableCell>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Execution Frequency</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											<p className="cal-txt">
												{jobData.executionFrequency}
												{jobData.otherScheduleInfo && (
													<Box mt={2}>{JSON.parse(jobData.otherScheduleInfo).frequencyOfExecution}</Box>
												)}
											</p>
										</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<img src={minisIcon} className="cal-icon" />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Box Name</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											{jobData.boxName ? (
												<a
													href={`/jobs/${jobData.boxId}`}
													onClick={() => {
														window.location.replace(`/jobs/${jobData.boxId}`);
													}}
												>
													<OverflowTooltip>{jobData.boxName}</OverflowTooltip>
												</a>
											) : (
												'NA'
											)}
										</Typography>
									</TableCell>
									<TableCell className="emptyCell"></TableCell>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Schedule Time</Typography>
									</TableCell>
									<TableCell className="attrValue scheduleTime">
										<Typography variant="bodyText">
											<p className="cal-txt">
												{jobData.scheduleTime || (jobData.latestRun && jobData.latestRun.scheduleTime)}
												{jobData.otherScheduleInfo && <Box mt={2}>{JSON.parse(jobData.otherScheduleInfo).time}</Box>}
											</p>
										</Typography>
									</TableCell>
									<TableCell className="attrValue scheduleTime">
										<Typography variant="bodyText">
											{jobData.scheduleTime ? <img src={timerIcon} className="cal-icon" /> : ''}
										</Typography>
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Job Type</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.jobType}</Typography>
									</TableCell>
									<TableCell className="emptyCell"></TableCell>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Last Successful Run</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											{jobData.lastSuccessRun && moment(jobData.lastSuccessRun).format()}
										</Typography>
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Machine</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.machine}</Typography>
									</TableCell>
									<TableCell className="emptyCell"></TableCell>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Avg Exec Duration</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											{jobData.latestRun && convertSeconds(jobData.latestRun.avgRunTime)}
										</Typography>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</Box>
				</Paper>
				<div className="job-execution-iframe-div">
					<AnalyticsVisualization
						params={{
							dashboardUrl: dashboardUrl.jobExecAndSla,
							className: 'jobExecutionIframeContainer',
							otherParams: {
								from: 'now-6h',
								to: 'now',
								'var-jobId': jobId
							}
						}}
					/>
					{/* </Paper> */}
				</div>
			</Stack>

			<Stack direction="row" alignItems="flextStart" spacing={2} sx={{ paddingRight: '0px' }}>
				<Paper elevation={8} className="box-panel width-65 h-400" style={{ height: '400px !important' }}>
					<div className="edit-icon--wrap">
						<IconButton className="edit-icon--style" aria-label="edit" onClick={handleEditOpen} color="primary">
							<EditIcon />
						</IconButton>
					</div>
					<Box p={2}>
						<Typography variant="subHeader" component="h6">
							Metadata
						</Typography>

						<Table className="job-overview-table">
							<TableBody>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Job Alias</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.friendlyName}</Typography>
									</TableCell>
									<TableCell className="emptyCell" />
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Estimated Duration</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.estimatedDuration}</Typography>
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Description</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.description}</Typography>
									</TableCell>
									<TableCell className="emptyCell"></TableCell>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Normal Completion Time</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											{jobData.slaEndTime ? (
												<OverflowTooltip customTooltip={`Timezone: ${moment().isDST() ? 'EDT(UTC-4)' : 'EST(UTC-5)'}`}>
													<p className="cal-txt">{jobData.slaEndTime}</p>
												</OverflowTooltip>
											) : (
												''
											)}
										</Typography>
									</TableCell>
									<TableCell className="attrValue">
										{jobData.slaEndTime ? <img src={timerIcon} className="cal-icon" /> : ''}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Sector</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.sector}</Typography>
									</TableCell>
									<TableCell className="emptyCell" />
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Critical Job</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">
											{jobData.criticalFlag ? 'Yes' : 'No'}
										</Typography>
									</TableCell>
								</TableRow>

								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Process</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.process}</Typography>
									</TableCell>
								</TableRow>

								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">System</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.system}</Typography>
									</TableCell>
								</TableRow>

								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Application</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.application}</Typography>
									</TableCell>
								</TableRow>

								<TableRow>
									<TableCell className="attrTitle">
										<Typography variant="bodyText">Sub Application</Typography>
									</TableCell>
									<TableCell className="attrValue">
										<Typography variant="bodyText">{jobData.subApplication}</Typography>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</Box>
				</Paper>
				{jobData.latestRun ? (
					<Paper elevation={8} className="box-panel width-30 h-400">
						<Box p={2}>
							<Typography variant="subHeader" component="h6">
								Recent Run
							</Typography>

							<Table className="job-overview-table">
								<TableBody>
									<TableRow>
										<TableCell className="attrTitle">
											<Typography variant="bodyText">Status</Typography>
										</TableCell>
										<TableCell className="attrValue">
											<Typography variant="bodyText">{renderStatus(jobData.latestRun.runStatus)}</Typography>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="attrTitle">
											<Typography variant="bodyText">Start Time</Typography>
										</TableCell>
										<TableCell className="attrValue">
											<Typography variant="bodyText">
												<p className="cal-txt">
													{jobData.latestRun.runStartTime && moment(jobData.latestRun.runStartTime).format()}
												</p>
												<img src={calenderIcon} className="cal-icon" />
											</Typography>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="attrTitle">
											<Typography variant="bodyText">End time</Typography>
										</TableCell>
										<TableCell className="attrValue">
											<Typography variant="bodyText">
												<p className="cal-txt">
													{jobData.latestRun.runEndTime && moment(jobData.latestRun.runEndTime).format()}
												</p>
												<img src={calenderIcon} className="cal-icon" />
											</Typography>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="attrTitle">
											<Typography variant="bodyText">SLA Status</Typography>
										</TableCell>
										<TableCell className="attrValue">
											{jobData.latestRun.slaStatus ? (
												<Chip
													variant="chipLabel"
													label={jobData.latestRun.slaStatus}
													color={jobData.latestRun.slaStatus === 'Met' ? 'success' : 'secondary'}
												/>
											) : (
												'NA'
											)}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="attrTitle">
											<Typography variant="bodyText">Follow Up</Typography>
										</TableCell>
										<TableCell className="attrValue">
											<Typography variant="bodyText">{jobData.latestRun.downstream}</Typography>
										</TableCell>
									</TableRow>

									<TableRow>
										<TableCell className="attrTitle">
											<Typography variant="bodyText">Reason</Typography>
										</TableCell>
										<TableCell className="attrValue">
											<Typography variant="bodyText">{jobData.latestRun.reasonForDelay}</Typography>
										</TableCell>
									</TableRow>

									<TableRow>
										<TableCell className="attrTitle">
											<Typography variant="bodyText">Action Taken</Typography>
										</TableCell>
										<TableCell className="attrValue">
											<Typography variant="bodyText">{jobData.latestRun.actionTaken}</Typography>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</Box>
					</Paper>
				) : (
					''
				)}
			</Stack>
			<Dialog open={editOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md" className="edit-job-modal">
				<EditJob jobData={jobData} handleEditClose={handleEditClose} />
			</Dialog>
		</Stack>
	);
}
